import { useState } from 'react';
import { Target, Heart, Sparkles, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Goal, WishlistItem, RecurringExpense } from '../types';
import { format } from 'date-fns';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp, updateDoc, doc, getDocs, query, where } from 'firebase/firestore';

interface GoalsPlannerProps {
  goals: Goal[];
  wishlist: WishlistItem[];
  householdId: string;
  userId: string;
  onEditGoal: (goal: Goal) => void;
  onEditWishlist: (item: WishlistItem) => void;
}

export default function GoalsPlanner({ goals, wishlist, householdId, userId, onEditGoal, onEditWishlist }: GoalsPlannerProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);

  const handleGeneratePlan = async () => {
    setIsGenerating(true);
    try {
      const generatePlan = httpsCallable(functions, 'generateSavingsPlan');
      await generatePlan();
      // Firestore listeners in Dashboard will auto-update the UI
    } catch (err) {
      console.error('Failed to generate savings plan:', err);
      alert('Failed to generate AI strategy. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAcceptSuggestion = async (itemId: string, type: 'goal' | 'wishlist', name: string, amount: number) => {
    setAcceptingId(itemId);
    try {
      // Check if a recurring expense already exists for this linked item
      const expensesRef = collection(db, 'recurring_expenses');
      const q = query(expensesRef, where('householdId', '==', householdId), where(type === 'goal' ? 'linkedGoalId' : 'linkedWishlistId', '==', itemId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Update existing
        const existingDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, 'recurring_expenses', existingDoc.id), {
          amount,
        });
        alert(`Updated existing recurring expense for ${name} to $${amount.toFixed(2)}/month.`);
      } else {
        // Create new
        const newExpense: Partial<RecurringExpense> = {
          userId,
          householdId,
          category: type === 'goal' ? 'Savings Contribution' : 'Wishlist Funding',
          name: name,
          amount: amount,
          schedule: { frequency: 'monthly' },
          createdAt: new Date(),
        };
        
        if (type === 'goal') {
          newExpense.linkedGoalId = itemId;
        } else {
          newExpense.linkedWishlistId = itemId;
        }

        await addDoc(collection(db, 'recurring_expenses'), {
          ...newExpense,
          createdAt: serverTimestamp()
        });
        alert(`Created a new recurring expense for ${name} at $${amount.toFixed(2)}/month.`);
      }
    } catch (err) {
      console.error('Failed to accept suggestion:', err);
      alert('Failed to accept suggestion. Please try again.');
    } finally {
      setAcceptingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-6xl mx-auto w-full">
      <div className="bg-gradient-to-r from-indigo-900 to-purple-900 rounded-xl p-6 md:p-8 text-white shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 mb-2">
            <Sparkles className="w-6 h-6 text-indigo-400" />
            AI Strategy Planner
          </h2>
          <p className="text-indigo-100 max-w-xl text-sm leading-relaxed">
            Let the AI analyze your income, fixed expenses, and debt minimums to suggest the optimal monthly contributions for your goals and wishlist items.
          </p>
        </div>
        <button
          onClick={handleGeneratePlan}
          disabled={isGenerating}
          className="flex-shrink-0 bg-white text-indigo-900 px-6 py-3 rounded-lg font-bold shadow-md hover:bg-indigo-50 transition-colors flex items-center gap-2 disabled:opacity-75 disabled:cursor-wait"
        >
          <RefreshCw className={`w-5 h-5 ${isGenerating ? 'animate-spin text-indigo-400' : ''}`} />
          {isGenerating ? 'Analyzing Cash Flow...' : 'Generate AI Savings Plan'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Goals Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
            <Target className="w-5 h-5 text-blue-600" />
            <h3 className="font-bold text-slate-800">Financial Goals</h3>
          </div>
          <div className="p-5 flex-1 overflow-y-auto">
            {goals.length === 0 ? (
              <p className="text-sm text-slate-500 italic text-center py-8">No goals set yet.</p>
            ) : (
              <div className="space-y-4">
                {goals.map(goal => {
                  const progress = Math.min(100, Math.round((goal.currentAmount / goal.targetAmount) * 100));
                  return (
                    <div key={goal.id} className="border border-slate-100 rounded-lg p-4 hover:border-blue-100 transition-colors group">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <button onClick={() => onEditGoal(goal)} className="font-bold text-slate-800 group-hover:text-blue-600 flex items-center gap-2 text-left">
                            {goal.name}
                            <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity uppercase font-bold tracking-wider">Edit</span>
                          </button>
                          <p className="text-xs text-slate-500 mt-1 capitalize">{goal.type} Goal • {goal.deadline ? `Target: ${format(goal.deadline, 'MMM yyyy')}` : 'No deadline'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-slate-900">${goal.currentAmount.toFixed(0)} <span className="text-slate-400 font-normal">/ ${goal.targetAmount.toFixed(0)}</span></p>
                          <p className="text-xs text-blue-600 font-bold mt-1">{progress}% Complete</p>
                        </div>
                      </div>
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${progress}%` }}></div>
                      </div>
                      
                      {goal.aiSuggestedSavings !== undefined && (
                        <div className="mt-3 p-3 bg-indigo-50/50 rounded-lg border border-indigo-100 flex items-center justify-between gap-4">
                          <div className="flex items-start gap-2">
                            <Sparkles className="w-4 h-4 text-indigo-500 mt-0.5" />
                            <div>
                              <p className="text-xs font-bold text-indigo-900">AI Suggestion</p>
                              <p className="text-sm font-black text-indigo-700">${goal.aiSuggestedSavings.toFixed(2)}<span className="text-xs font-normal text-indigo-500">/mo</span></p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleAcceptSuggestion(goal.id!, 'goal', goal.name, goal.aiSuggestedSavings!)}
                            disabled={acceptingId === goal.id}
                            className="text-xs font-bold bg-white border border-indigo-200 text-indigo-600 px-3 py-1.5 rounded-md shadow-sm hover:bg-indigo-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {acceptingId === goal.id ? 'Saving...' : 'Accept'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Wishlist Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2 bg-slate-50">
            <Heart className="w-5 h-5 text-rose-500" />
            <h3 className="font-bold text-slate-800">Family Wishlist</h3>
          </div>
          <div className="p-5 flex-1 overflow-y-auto">
            {wishlist.length === 0 ? (
              <p className="text-sm text-slate-500 italic text-center py-8">No wishlist items added yet.</p>
            ) : (
              <div className="space-y-4">
                {wishlist.map(item => (
                  <div key={item.id} className="border border-slate-100 rounded-lg p-4 hover:border-rose-100 transition-colors group">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <button onClick={() => onEditWishlist(item)} className="font-bold text-slate-800 group-hover:text-rose-600 flex items-center gap-2 text-left">
                          {item.name}
                          <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity uppercase font-bold tracking-wider">Edit</span>
                        </button>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${
                            item.priorityLevel === 'High' ? 'bg-rose-100 text-rose-700' :
                            item.priorityLevel === 'Medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {item.priorityLevel} Priority
                          </span>
                          {item.targetDate && (
                            <span className="text-xs text-slate-500">• Target: {format(item.targetDate, 'MMM yyyy')}</span>
                          )}
                        </div>
                      </div>
                      <p className="text-sm font-bold text-slate-900">${item.estimatedCost.toFixed(0)}</p>
                    </div>

                    {item.aiSuggestedSavings !== undefined && (
                      <div className="mt-3 p-3 bg-indigo-50/50 rounded-lg border border-indigo-100 flex items-center justify-between gap-4">
                        <div className="flex items-start gap-2">
                          <Sparkles className="w-4 h-4 text-indigo-500 mt-0.5" />
                          <div>
                            <p className="text-xs font-bold text-indigo-900">AI Suggestion</p>
                            <p className="text-sm font-black text-indigo-700">${item.aiSuggestedSavings.toFixed(2)}<span className="text-xs font-normal text-indigo-500">/mo</span></p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAcceptSuggestion(item.id!, 'wishlist', item.name, item.aiSuggestedSavings!)}
                          disabled={acceptingId === item.id}
                          className="text-xs font-bold bg-white border border-indigo-200 text-indigo-600 px-3 py-1.5 rounded-md shadow-sm hover:bg-indigo-50 transition-colors flex items-center gap-1 disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {acceptingId === item.id ? 'Saving...' : 'Accept'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
